import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Portal, IconButton, Tooltip, TextField, Checkbox, CircularProgress } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import {
    downloadSupplierInvoicesExcel,
    downloadSupplierInvoicesPdf,
} from '../../../Utils/supplierInvoicesExport';
import styles from './SupplierInvoicesModal.module.scss';

/** Mirrors SupplierInvoiceDTOs.MatchStatus. */
const STATUS = {
    UNMATCHED: 0,
    MATCHED: 1,
    LINKED: 2,
};

/** Mirrors SupplierInvoiceDTOs.ImportAction. */
const ACTION = {
    CREATE: 0,
    LINK: 1,
};

/** Mirrors SupplierInvoiceDTOs.InvoiceDateField (the API's `action` field). */
const DATE_FIELD = {
    INVOICE_DATE: 1,
    ISSUANCE_DATE: 2,
};

const FILTER_ALL = 'all';

/**
 * The Tax Authority identifies a supplier invoice by supplier plus reference, so that pair is what
 * row selection and the per-supplier name inputs are keyed on.
 */
const rowKey = (row) =>
    `${row?.invoice?.vatNumber ?? ''}|${(row?.invoice?.invoiceReferenceNumber ?? '').trim()}`;

const supplierKey = (row) => String(row?.invoice?.vatNumber ?? '');

// CustomDatePicker emits dates at UTC midnight and the page stores them that way, so every date
// here is read and written in UTC. Using local parts would shift the day for negative UTC offsets.
const startOfMonth = (date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));

const endOfMonth = (date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));

const toIsoDate = (date) => {
    if (!date) return null;
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
};

const formatDisplayDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('he-IL', { timeZone: 'UTC' });
};

const formatAmount = (value) => {
    const parsed = Number(value ?? 0);
    return Number.isNaN(parsed)
        ? '0.00'
        : parsed.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** The gross figure the server books as Expense.Amount, recomputed here only for display. */
const grossAmount = (invoice) => {
    const including = Number(invoice?.paymentAmountIncludingVat ?? 0);
    if (including) return including;
    return Number(invoice?.paymentAmount ?? 0) + Number(invoice?.vatAmount ?? 0);
};

const SupplierInvoicesModal = ({
    open,
    onClose,
    caseId,
    accountantId,
    clientName = '',
    token,
    initialStartDate = null,
    initialEndDate = null,
    direction = 'rtl',
    onImported,
    onCompare,
    onImport,
}) => {
    const { t, i18n } = useTranslation();
    const dir = direction || i18n.dir();

    const [startDate, setStartDate] = useState(() => initialStartDate || startOfMonth(new Date()));
    const [endDate, setEndDate] = useState(() => initialEndDate || endOfMonth(new Date()));
    const [supplierVatNumber, setSupplierVatNumber] = useState('');
    const [dateField, setDateField] = useState(DATE_FIELD.INVOICE_DATE);

    const [result, setResult] = useState(null);
    const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());
    const [supplierNames, setSupplierNames] = useState({});

    const [comparing, setComparing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(null);
    const [error, setError] = useState('');

    // A stale comparison must not be acted on after the modal is reopened for another period.
    useEffect(() => {
        if (open) return;
        setResult(null);
        setSelectedKeys(new Set());
        setSupplierNames({});
        setStatusFilter(FILTER_ALL);
        setError('');
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (initialStartDate) setStartDate(initialStartDate);
        if (initialEndDate) setEndDate(initialEndDate);
    }, [open, initialStartDate, initialEndDate]);

    const rows = result?.invoices ?? [];

    const visibleRows = useMemo(() => {
        if (statusFilter === FILTER_ALL) return rows;
        return rows.filter((row) => row.status === statusFilter);
    }, [rows, statusFilter]);

    /** Rows already carrying the allocation number have nothing left to apply. */
    const isActionable = useCallback((row) => row.status !== STATUS.LINKED, []);

    const selectableVisibleRows = useMemo(
        () => visibleRows.filter(isActionable),
        [visibleRows, isActionable]
    );

    const selectedRows = useMemo(
        () => rows.filter((row) => isActionable(row) && selectedKeys.has(rowKey(row))),
        [rows, selectedKeys, isActionable]
    );

    const allVisibleSelected =
        selectableVisibleRows.length > 0 &&
        selectableVisibleRows.every((row) => selectedKeys.has(rowKey(row)));

    const handleCompare = async () => {
        if (!caseId) return;
        setError('');
        setComparing(true);
        try {
            const data = await onCompare({
                caseId,
                startDate: toIsoDate(startDate),
                endDate: toIsoDate(endDate),
                supplierVatNumber: supplierVatNumber.trim() || null,
                dateField,
                includeItems: false,
            });

            setResult(data);
            setStatusFilter(FILTER_ALL);
            setSupplierNames({});
            // Everything that still needs an action starts selected; linked rows are excluded.
            setSelectedKeys(
                new Set((data?.invoices ?? []).filter(isActionable).map(rowKey))
            );
        } catch (err) {
            setResult(null);
            setSelectedKeys(new Set());
            setError(err?.message || t('SupplierInvoicesFetchError'));
        } finally {
            setComparing(false);
        }
    };

    const handleImport = async () => {
        if (selectedRows.length === 0) return;
        setError('');
        setImporting(true);
        try {
            const payload = selectedRows.map((row) => ({
                Invoice: row.invoice,
                Action: row.status === STATUS.UNMATCHED ? ACTION.CREATE : ACTION.LINK,
                ExpenseId: row.expense?.id ?? null,
                SortCodeId: row.suggestedSortCodeId ?? null,
                DebitAccount: row.suggestedDebitAccount ?? null,
                AccountCodeId: row.supplierCardId ?? null,
                AccountCodeName: supplierNames[supplierKey(row)]?.trim() || null,
            }));

            const response = await onImport({ caseId, accountantId, invoices: payload });
            onImported?.(response);

            // Re-run the comparison so the rows just written show as linked rather than going stale.
            await handleCompare();
        } catch (err) {
            setError(err?.message || t('SupplierInvoicesImportError'));
        } finally {
            setImporting(false);
        }
    };

    const dateBasisLabel =
        dateField === DATE_FIELD.ISSUANCE_DATE
            ? t('SupplierInvoiceIssuanceDate')
            : t('DocumentDate');

    const handleExport = async (format) => {
        if (!result || visibleRows.length === 0) return;
        setExporting(format);
        try {
            const args = {
                rows: visibleRows,
                clientName,
                clientCase: caseId,
                startDate,
                endDate,
                dateBasisLabel,
                supplierVatNumber,
                supplierNames,
                token,
                t,
            };
            if (format === 'pdf') await downloadSupplierInvoicesPdf(args);
            else downloadSupplierInvoicesExcel(args);
        } catch (err) {
            toast.error(err?.message || t('DownloadFailed'));
        } finally {
            setExporting(null);
        }
    };

    const toggleRow = (row) => {
        const key = rowKey(row);
        setSelectedKeys((previous) => {
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleAllVisible = () => {
        setSelectedKeys((previous) => {
            const next = new Set(previous);
            if (allVisibleSelected) {
                selectableVisibleRows.forEach((row) => next.delete(rowKey(row)));
            } else {
                selectableVisibleRows.forEach((row) => next.add(rowKey(row)));
            }
            return next;
        });
    };

    const statusBadge = (row) => {
        if (row.status === STATUS.LINKED) {
            return (
                <span className={`${styles.badge} ${styles.badgeLinked}`}>
                    {t('SupplierInvoiceAlreadyLinked')}
                </span>
            );
        }
        if (row.status === STATUS.MATCHED) {
            return (
                <span className={`${styles.badge} ${styles.badgeMatched}`}>
                    {t('SupplierInvoiceMatched')}
                </span>
            );
        }
        return (
            <span className={`${styles.badge} ${styles.badgeUnmatched}`}>
                {t('SupplierInvoiceMissing')}
            </span>
        );
    };

    const renderSystemCell = (row) => {
        if (row.expense) {
            return (
                <>
                    <div className={styles.supplierName}>
                        {t('UploadNumber')} {row.expense.uploadNumber}
                    </div>
                    {row.expense.description && (
                        <div className={styles.subtle}>{row.expense.description}</div>
                    )}
                    {row.expense.differences?.length > 0 && (
                        <div className={styles.differences}>
                            {row.expense.differences.map((difference) => (
                                <span key={difference.field} className={styles.difference}>
                                    {t(`SupplierInvoiceField_${difference.field}`, {
                                        defaultValue: difference.field,
                                    })}
                                    {': '}
                                    {difference.systemValue || '—'} → {difference.taxAuthorityValue || '—'}
                                </span>
                            ))}
                        </div>
                    )}
                </>
            );
        }

        // Nothing on file: a new expense is created, and the Tax Authority never sends a supplier
        // name, so this is the only chance to name a card that does not exist yet.
        if (row.supplierCardId) {
            return <span className={styles.subtle}>{t('SupplierInvoiceWillBeCreated')}</span>;
        }

        const key = supplierKey(row);
        return (
            <TextField
                value={supplierNames[key] ?? ''}
                onChange={(event) =>
                    setSupplierNames((previous) => ({ ...previous, [key]: event.target.value }))
                }
                placeholder={t('SupplierInvoiceNewSupplierName')}
                size="small"
                variant="standard"
                fullWidth
                className={`${fieldStyles.styleInput} ${styles.nameInput}`}
            />
        );
    };

    const summaryTabs = [
        { id: FILTER_ALL, label: t('All'), count: result?.totalCount ?? 0 },
        { id: STATUS.UNMATCHED, label: t('SupplierInvoiceMissing'), count: result?.unmatchedCount ?? 0 },
        { id: STATUS.MATCHED, label: t('SupplierInvoiceMatched'), count: result?.matchedCount ?? 0 },
        { id: STATUS.LINKED, label: t('SupplierInvoiceAlreadyLinked'), count: result?.linkedCount ?? 0 },
    ];

    if (!open) return null;

    const busy = comparing || importing || exporting !== null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${dir === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={busy ? undefined : onClose}
            >
                <div dir={dir} onClick={(event) => event.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.body}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close')}>
                                    <IconButton onClick={onClose} disabled={busy} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('SupplierInvoicesFromTaxAuthority')}</div>
                                <div />
                            </div>

                            <div className={styles.filters}>
                                <div className={`${styles.field} ${styles.fieldSm}`}>
                                    <span className={styles.fieldLabel}>{t('StartDate')}</span>
                                    <CustomDatePicker
                                        date={startDate}
                                        onDateChange={setStartDate}
                                        label=""
                                        height="34px"
                                    />
                                </div>
                                <div className={`${styles.field} ${styles.fieldSm}`}>
                                    <span className={styles.fieldLabel}>{t('EndDate')}</span>
                                    <CustomDatePicker
                                        date={endDate}
                                        onDateChange={setEndDate}
                                        label=""
                                        height="34px"
                                    />
                                </div>
                                <div className={`${styles.field} ${styles.fieldSm}`}>
                                    <span className={styles.fieldLabel}>
                                        {t('SupplierInvoiceSupplierVatFilter')}
                                    </span>
                                    <TextField
                                        value={supplierVatNumber}
                                        onChange={(event) => setSupplierVatNumber(event.target.value)}
                                        placeholder={t('AllSuppliers')}
                                        size="small"
                                        variant="standard"
                                        fullWidth
                                        className={`${fieldStyles.styleInput} ${styles.compactInput}`}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>{t('SupplierInvoiceDateBasis')}</span>
                                    <div className={styles.segmented}>
                                        <button
                                            type="button"
                                            className={`${styles.segment}${
                                                dateField === DATE_FIELD.INVOICE_DATE
                                                    ? ` ${styles.segmentActive}`
                                                    : ''
                                            }`}
                                            onClick={() => setDateField(DATE_FIELD.INVOICE_DATE)}
                                        >
                                            {t('DocumentDate')}
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.segment}${
                                                dateField === DATE_FIELD.ISSUANCE_DATE
                                                    ? ` ${styles.segmentActive}`
                                                    : ''
                                            }`}
                                            onClick={() => setDateField(DATE_FIELD.ISSUANCE_DATE)}
                                        >
                                            {t('SupplierInvoiceIssuanceDate')}
                                        </button>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={styles.secondaryBtn}
                                    onClick={handleCompare}
                                    disabled={busy || !startDate || !endDate}
                                >
                                    {comparing ? (
                                        <CircularProgress className={styles.spinner} size={18} color="inherit" />
                                    ) : (
                                        <>
                                            <SearchIcon fontSize="small" />
                                            {t('SupplierInvoiceCompare')}
                                        </>
                                    )}
                                </button>
                            </div>

                            {error && (
                                <div className={styles.error}>
                                    <ErrorOutlineIcon fontSize="small" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {result && (
                                <div className={styles.summary}>
                                    {summaryTabs.map((tab) => (
                                        <button
                                            key={String(tab.id)}
                                            type="button"
                                            className={`${styles.summaryTab}${
                                                statusFilter === tab.id ? ` ${styles.summaryTabActive}` : ''
                                            }`}
                                            onClick={() => setStatusFilter(tab.id)}
                                        >
                                            {tab.label}
                                            <span className={styles.summaryCount}>{tab.count}</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {!result && !comparing && (
                                <div className={styles.empty}>
                                    <p className={styles.emptyTitle}>{t('SupplierInvoicesFromTaxAuthority')}</p>
                                    <p className={styles.emptyText}>{t('SupplierInvoicesIntro')}</p>
                                </div>
                            )}

                            {result && visibleRows.length === 0 && (
                                <div className={styles.empty}>
                                    <p className={styles.emptyTitle}>{t('NoResultsFound')}</p>
                                    <p className={styles.emptyText}>{t('SupplierInvoicesNoRowsForFilter')}</p>
                                </div>
                            )}

                            {result && visibleRows.length > 0 && (
                                <div className={styles.scroll}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <Checkbox
                                                        size="small"
                                                        checked={allVisibleSelected}
                                                        indeterminate={
                                                            !allVisibleSelected &&
                                                            selectableVisibleRows.some((row) =>
                                                                selectedKeys.has(rowKey(row))
                                                            )
                                                        }
                                                        onChange={toggleAllVisible}
                                                        disabled={selectableVisibleRows.length === 0}
                                                    />
                                                </th>
                                                <th>{t('Supplier')}</th>
                                                <th>{t('Reference')}</th>
                                                <th>{t('Date')}</th>
                                                <th>{t('Amount')}</th>
                                                <th>{t('VAT')}</th>
                                                <th>{t('ConfirmationNumber')}</th>
                                                <th>{t('Status')}</th>
                                                <th>{t('SupplierInvoiceInSystem')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleRows.map((row) => {
                                                const key = rowKey(row);
                                                const selected = selectedKeys.has(key);
                                                const amount = grossAmount(row.invoice);
                                                return (
                                                    <tr
                                                        key={key}
                                                        className={selected ? styles.rowSelected : undefined}
                                                    >
                                                        <td>
                                                            <Checkbox
                                                                size="small"
                                                                checked={selected}
                                                                onChange={() => toggleRow(row)}
                                                                disabled={!isActionable(row)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <div className={styles.supplierName}>
                                                                {row.supplierCardName || t('UnknownSupplier')}
                                                            </div>
                                                            <div className={styles.subtle}>
                                                                {row.invoice.vatNumber}
                                                            </div>
                                                        </td>
                                                        <td>{row.invoice.invoiceReferenceNumber}</td>
                                                        <td className={styles.numeric}>
                                                            {formatDisplayDate(row.invoice.invoiceDate)}
                                                        </td>
                                                        <td
                                                            className={`${styles.numeric}${
                                                                amount < 0 ? ` ${styles.negative}` : ''
                                                            }`}
                                                        >
                                                            {formatAmount(amount)}
                                                        </td>
                                                        <td className={styles.numeric}>
                                                            {formatAmount(row.invoice.vatAmount)}
                                                        </td>
                                                        <td className={styles.subtle}>
                                                            {row.invoice.confirmationNumber || '—'}
                                                        </td>
                                                        <td>{statusBadge(row)}</td>
                                                        <td>{renderSystemCell(row)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className={styles.footer}>
                                {result && (
                                    <span className={styles.footerSpacer}>
                                        {t('SupplierInvoicesSelectedSummary', {
                                            selected: selectedRows.length,
                                            create: selectedRows.filter(
                                                (row) => row.status === STATUS.UNMATCHED
                                            ).length,
                                            link: selectedRows.filter((row) => row.status === STATUS.MATCHED)
                                                .length,
                                        })}
                                    </span>
                                )}
                                {result && (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.ghostBtn}
                                            onClick={() => handleExport('pdf')}
                                            disabled={busy || visibleRows.length === 0}
                                        >
                                            {exporting === 'pdf' ? (
                                                <CircularProgress className={styles.spinner} size={18} color="inherit" />
                                            ) : (
                                                <PictureAsPdfIcon fontSize="small" />
                                            )}
                                            {t('DownloadPDFFile')}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.ghostBtn}
                                            onClick={() => handleExport('excel')}
                                            disabled={busy || visibleRows.length === 0}
                                        >
                                            {exporting === 'excel' ? (
                                                <CircularProgress className={styles.spinner} size={18} color="inherit" />
                                            ) : (
                                                <TableChartIcon fontSize="small" />
                                            )}
                                            {t('DownloadExcelFile')}
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className={styles.secondaryBtn}
                                    onClick={onClose}
                                    disabled={busy}
                                >
                                    {t('cancelButton')}
                                </button>
                                <button
                                    type="button"
                                    className={styles.primaryBtn}
                                    onClick={handleImport}
                                    disabled={busy || selectedRows.length === 0}
                                >
                                    {importing ? (
                                        <CircularProgress className={styles.spinner} size={18} color="inherit" />
                                    ) : (
                                        t('SupplierInvoiceApplySelected')
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default SupplierInvoicesModal;
